export type PortalFileFolderColor =
  | 'blue'
  | 'red'
  | 'amber'
  | 'pink'
  | 'green'
  | 'violet'
  | 'orange'
  | 'sky'
  | 'slate';

export type PortalFileType = 'docx' | 'pdf' | 'fig' | 'xlsx' | 'folder' | 'png' | 'pptx';

export type PortalFileComment = {
  id: string;
  authorName: string;
  body: string;
  whenLabel: string;
  replies: PortalFileComment[];
};

export type PortalProjectFile = {
  id: string;
  name: string;
  folderId: string;
  type: PortalFileType;
  typeLabel: string;
  versionLabel: string;
  updatedAtLabel: string;
  createdAtLabel: string;
  ownerName: string;
  sizeLabel: string;
  description: string;
  subtitle: string;
  statusLabel: string;
  permissionsLabel: string;
  viewUrl: string;
  downloadUrl: string;
  linkedProject: string;
  linkedDelivery?: string;
  linkedActivity?: string;
  relatedFileIds: string[];
  comments: PortalFileComment[];
};

export type PortalFileFolder = {
  id: string;
  name: string;
  color: PortalFileFolderColor;
};

export type PortalFilesData = {
  projectId: string;
  folders: PortalFileFolder[];
  files: PortalProjectFile[];
};

export const portalFilesByProjectId: Record<string, PortalFilesData> = {
  crm: {
    projectId: 'crm',
    folders: [
      { id: 'all', name: 'Todos os arquivos', color: 'blue' },
      { id: 'main', name: 'Documentos principais', color: 'red' },
      { id: 'planning', name: 'Planejamento', color: 'amber' },
      { id: 'requirements', name: 'Requisitos', color: 'pink' },
      { id: 'design', name: 'Design e Protótipos', color: 'green' },
      { id: 'integration', name: 'Integração', color: 'violet' },
      { id: 'training', name: 'Capacitação', color: 'orange' },
      { id: 'contracts', name: 'Contratos', color: 'sky' },
    ],
    files: [
      {
        id: 'file-plan',
        name: 'Plano do projeto',
        folderId: 'main',
        type: 'docx',
        typeLabel: 'DOCX',
        versionLabel: 'v1.3',
        updatedAtLabel: '27/10/2026',
        createdAtLabel: '01/09/2026',
        ownerName: 'Mariana Silva',
        sizeLabel: '2.4 MB',
        description: 'Plano macro do projeto com fases, marcos e responsáveis.',
        subtitle: 'Documento principal',
        statusLabel: 'Aprovado',
        permissionsLabel: 'Leitura para o cliente',
        viewUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        downloadUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        linkedProject: 'Implementação CRM',
        linkedDelivery: 'Análise de requisitos',
        linkedActivity: 'Kick-off do projeto',
        relatedFileIds: ['file-requirements', 'file-contract'],
        comments: [
          {
            id: 'fc1',
            authorName: 'Mariana Silva',
            body: 'Versão atualizada com o novo marco de homologação.',
            whenLabel: 'há 2 horas',
            replies: [
              {
                id: 'fc1r1',
                authorName: 'João Santos',
                body: 'Perfeito. Vou alinhar com o time comercial.',
                whenLabel: 'há 1 hora',
                replies: [],
              },
            ],
          },
          {
            id: 'fc2',
            authorName: 'Ana Souza',
            body: 'Incluí a referência do ambiente de testes na seção 4.',
            whenLabel: 'ontem',
            replies: [],
          },
        ],
      },
      {
        id: 'file-requirements',
        name: 'Requisitos funcionais',
        folderId: 'requirements',
        type: 'pdf',
        typeLabel: 'PDF',
        versionLabel: 'v1.2',
        updatedAtLabel: '15/09/2026',
        createdAtLabel: '05/09/2026',
        ownerName: 'Rafael Oliveira',
        sizeLabel: '1.1 MB',
        description: 'Especificação funcional aprovada na etapa de planejamento.',
        subtitle: 'Requisitos',
        statusLabel: 'Aprovado',
        permissionsLabel: 'Leitura para o cliente',
        viewUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        downloadUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        linkedProject: 'Implementação CRM',
        linkedDelivery: 'Análise de requisitos',
        relatedFileIds: ['file-plan'],
        comments: [],
      },
      {
        id: 'file-dashboard',
        name: 'Tela de dashboard',
        folderId: 'design',
        type: 'fig',
        typeLabel: 'FIG',
        versionLabel: 'v0.9',
        updatedAtLabel: '22/10/2026',
        createdAtLabel: '10/10/2026',
        ownerName: 'Ana Souza',
        sizeLabel: '4.8 MB',
        description: 'Protótipo das telas do dashboard comercial.',
        subtitle: 'Design',
        statusLabel: 'Em revisão',
        permissionsLabel: 'Leitura para o cliente',
        viewUrl: 'https://www.figma.com',
        downloadUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        linkedProject: 'Implementação CRM',
        linkedActivity: 'Desenvolvimento dos módulos',
        relatedFileIds: [],
        comments: [
          {
            id: 'fc3',
            authorName: 'Ricardo Alves',
            body: 'Podemos destacar o funil na primeira dobra?',
            whenLabel: 'há 3 horas',
            replies: [],
          },
        ],
      },
      {
        id: 'file-cronograma',
        name: 'Cronograma de outubro',
        folderId: 'planning',
        type: 'xlsx',
        typeLabel: 'XLSX',
        versionLabel: 'v1.0',
        updatedAtLabel: '04/10/2026',
        createdAtLabel: '04/10/2026',
        ownerName: 'Mariana Silva',
        sizeLabel: '340 KB',
        description: 'Cronograma detalhado das entregas de outubro.',
        subtitle: 'Planejamento',
        statusLabel: 'Compartilhado',
        permissionsLabel: 'Leitura para o cliente',
        viewUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        downloadUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        linkedProject: 'Implementação CRM',
        relatedFileIds: ['file-plan'],
        comments: [],
      },
      {
        id: 'file-contract',
        name: 'Contrato de prestação de serviços',
        folderId: 'contracts',
        type: 'pdf',
        typeLabel: 'PDF',
        versionLabel: 'v1.0',
        updatedAtLabel: '20/08/2026',
        createdAtLabel: '20/08/2026',
        ownerName: 'João Santos',
        sizeLabel: '890 KB',
        description: 'Contrato comercial vigente do projeto.',
        subtitle: 'Contrato',
        statusLabel: 'Aprovado',
        permissionsLabel: 'Leitura restrita',
        viewUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        downloadUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        linkedProject: 'Implementação CRM',
        relatedFileIds: ['file-plan'],
        comments: [],
      },
      {
        id: 'file-manual',
        name: 'Manual de testes',
        folderId: 'training',
        type: 'pdf',
        typeLabel: 'PDF',
        versionLabel: 'v1.1',
        updatedAtLabel: '25/10/2026',
        createdAtLabel: '18/10/2026',
        ownerName: 'Ana Souza',
        sizeLabel: '720 KB',
        description: 'Roteiro de testes para a fase de homologação.',
        subtitle: 'Capacitação',
        statusLabel: 'Compartilhado',
        permissionsLabel: 'Leitura para o cliente',
        viewUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        downloadUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        linkedProject: 'Implementação CRM',
        linkedDelivery: 'Ambiente de testes configurado',
        relatedFileIds: [],
        comments: [],
      },
      {
        id: 'file-api-map',
        name: 'Mapeamento da API ERP',
        folderId: 'integration',
        type: 'docx',
        typeLabel: 'DOCX',
        versionLabel: 'v0.8',
        updatedAtLabel: '21/10/2026',
        createdAtLabel: '12/10/2026',
        ownerName: 'Carlos Mendes',
        sizeLabel: '510 KB',
        description: 'Campos e endpoints da integração com o ERP.',
        subtitle: 'Integração',
        statusLabel: 'Em andamento',
        permissionsLabel: 'Leitura para o cliente',
        viewUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        downloadUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        linkedProject: 'Implementação CRM',
        linkedActivity: 'Integração com ERP',
        relatedFileIds: ['file-requirements'],
        comments: [],
      },
      {
        id: 'file-wireframe',
        name: 'Wireframes de oportunidades',
        folderId: 'design',
        type: 'png',
        typeLabel: 'PNG',
        versionLabel: 'v1.0',
        updatedAtLabel: '19/10/2026',
        createdAtLabel: '15/10/2026',
        ownerName: 'Ana Souza',
        sizeLabel: '1.6 MB',
        description: 'Fluxos visuais do cadastro de oportunidades.',
        subtitle: 'Design',
        statusLabel: 'Compartilhado',
        permissionsLabel: 'Leitura para o cliente',
        viewUrl: 'https://www.w3.org/Graphics/PNG/nurbcup2s.png',
        downloadUrl: 'https://www.w3.org/Graphics/PNG/nurbcup2s.png',
        linkedProject: 'Implementação CRM',
        relatedFileIds: ['file-dashboard'],
        comments: [],
      },
    ],
  },
};
